/**
 * clientOtpFlow — pure logic for the "No Google account? Use a code" fallback
 * on the unified /login screen.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  Unified-login model (locked, #1190): the PRIMARY client path is Google /
 *  Apple sign-in at /login. A client with no Gmail needs a self-serve path to
 *  the 4-digit code fallback whose BACKEND is already live (requestClientOtp /
 *  verifyClientOtp in functions/avbp_client_otp.js, region asia-south1). This
 *  module is the pure, browser-free, unit-testable core of that UI flow:
 *    · validate / normalise the handle (email · phone · client# · project#)
 *    · OTP-digit state transitions (type-forward, backspace, paste-fill)
 *    · interpret the callable responses into a small phase machine
 *
 *  The .tsx (Login.tsx) does ONLY the wiring: it owns the httpsCallable round
 *  trips, signInWithCustomToken, the token refresh, and the navigate — none of
 *  which belong here. Keeping the logic pure mirrors lib/platform.ts (the
 *  Apple-detector split) so the node test runner can exercise it directly.
 *
 *  NO new callable is introduced — the response SHAPES below mirror the live
 *  requestClientOtp / verifyClientOtp contracts verbatim.
 * ─────────────────────────────────────────────────────────────────────────
 */

/** Number of OTP digits (matches the live 4-digit backend). */
export const OTP_LEN = 4;

/** The live `requestClientOtp({ handle })` response contract. */
export type RequestOtpResp =
  | { ok: true; sentTo: { email: string | null; phone: string | null }; otpId: string }
  | { ok: false; found: false }
  | { ok: false; locked: true; until: number }
  | { ok: false; error: string };

/** The live `verifyClientOtp({ handle, otp })` response contract. */
export type VerifyOtpResp =
  | {
      ok: true;
      customToken: string;
      claims: { isClient: true; contactId: string; clientId: string; firmId: string | null };
    }
  | { ok: false; attemptsLeft: number }
  | { ok: false; locked: true; until: number }
  | { ok: false; error: string };

/** The phases the code fallback walks through. */
export type CodePhase =
  | 'enter_handle'
  | 'enter_otp'
  | 'locked'
  | 'unknown'
  | 'delivery_failed';

/** `true` when the handle is non-empty after trimming. */
export function isValidHandle(handle: string): boolean {
  return typeof handle === 'string' && handle.trim().length > 0;
}

/** Trim + collapse internal whitespace runs (the backend resolver is
 *  whitespace-tolerant, but we send a tidy handle). */
export function normaliseHandle(handle: string): string {
  return String(handle ?? '').trim().replace(/\s+/g, ' ');
}

/** `true` once every OTP cell is filled with a single digit. */
export function isOtpComplete(digits: string[]): boolean {
  return (
    Array.isArray(digits) &&
    digits.length === OTP_LEN &&
    digits.every((d) => /^\d$/.test(d))
  );
}

/** Join the OTP cells into the 4-char string the callable expects. */
export function joinOtp(digits: string[]): string {
  return (digits || []).join('');
}

/** A fresh, empty OTP-cell array. */
export function blankOtp(): string[] {
  return Array(OTP_LEN).fill('');
}

/**
 * Apply a single-cell edit. Returns the next digit array AND the index that
 * focus should advance to (-1 = stay). Rejects non-digit input (returns the
 * array unchanged so the field never shows a letter).
 */
export function setOtpDigit(
  digits: string[],
  index: number,
  raw: string,
): { digits: string[]; focus: number; complete: boolean } {
  // Reject anything that is not a single digit or an empty (delete) string.
  if (!/^\d?$/.test(raw)) {
    return { digits, focus: -1, complete: isOtpComplete(digits) };
  }
  const next = [...digits];
  next[index] = raw;
  const focus = raw && index < OTP_LEN - 1 ? index + 1 : -1;
  return { digits: next, focus, complete: isOtpComplete(next) };
}

/**
 * Backspace handling: when the current cell is empty, focus moves to the
 * previous cell. Returns the index to focus, or -1 to stay put.
 */
export function backspaceFocus(digits: string[], index: number): number {
  if (index > 0 && !digits[index]) return index - 1;
  return -1;
}

/**
 * Distribute a pasted string (e.g. "1234" from an autofill) across the cells.
 * Non-digits are stripped; only the first OTP_LEN digits are kept.
 */
export function fillFromPaste(raw: string): {
  digits: string[];
  complete: boolean;
} {
  const onlyDigits = String(raw || '').replace(/\D/g, '').slice(0, OTP_LEN);
  const digits = blankOtp();
  for (let i = 0; i < onlyDigits.length; i++) digits[i] = onlyDigits[i];
  return { digits, complete: isOtpComplete(digits) };
}

/** Outcome of interpreting a requestClientOtp response. */
export interface RequestOutcome {
  phase: CodePhase;
  /** masked email the code was sent to (success only). */
  maskedEmail: string | null;
  /** masked phone the code was sent to (success only). */
  maskedPhone: string | null;
  /** lockout end (locked only). */
  lockUntil: number | null;
  /** a user-facing error message, or null. */
  error: string | null;
}

/** Map a requestClientOtp response into the next phase + UI fields. */
export function interpretRequest(resp: RequestOtpResp): RequestOutcome {
  if (resp.ok) {
    return {
      phase: 'enter_otp',
      maskedEmail: resp.sentTo?.email ?? null,
      maskedPhone: resp.sentTo?.phone ?? null,
      lockUntil: null,
      error: null,
    };
  }
  if ('found' in resp && resp.found === false) {
    return { phase: 'unknown', maskedEmail: null, maskedPhone: null, lockUntil: null, error: null };
  }
  if ('locked' in resp && resp.locked) {
    return { phase: 'locked', maskedEmail: null, maskedPhone: null, lockUntil: resp.until, error: null };
  }
  if ('error' in resp && resp.error === 'delivery_failed') {
    return { phase: 'delivery_failed', maskedEmail: null, maskedPhone: null, lockUntil: null, error: null };
  }
  if ('error' in resp && resp.error === 'no_destinations') {
    return {
      phase: 'enter_handle',
      maskedEmail: null,
      maskedPhone: null,
      lockUntil: null,
      error: 'We have no email or phone on file for this account. Please contact us.',
    };
  }
  return {
    phase: 'enter_handle',
    maskedEmail: null,
    maskedPhone: null,
    lockUntil: null,
    error: 'Something went wrong. Please try again.',
  };
}

/** Outcome of interpreting a verifyClientOtp response (non-success branches). */
export interface VerifyFailure {
  phase: CodePhase;
  lockUntil: number | null;
  attemptsLeft: number | null;
  error: string | null;
  /** true → clear the entered digits and refocus the first cell. */
  resetDigits: boolean;
}

/**
 * Interpret a NON-success verifyClientOtp response. (Success is handled in the
 * .tsx — it owns signInWithCustomToken.) Returns the phase + messaging.
 */
export function interpretVerifyFailure(
  resp: Exclude<VerifyOtpResp, { ok: true }>,
): VerifyFailure {
  if ('locked' in resp && resp.locked) {
    return { phase: 'locked', lockUntil: resp.until, attemptsLeft: null, error: null, resetDigits: false };
  }
  if ('attemptsLeft' in resp) {
    const n = resp.attemptsLeft;
    return {
      phase: 'enter_otp',
      lockUntil: null,
      attemptsLeft: n,
      error: `Incorrect code. ${n} attempt${n === 1 ? '' : 's'} left.`,
      resetDigits: true,
    };
  }
  if ('error' in resp && resp.error === 'no_otp') {
    return {
      phase: 'enter_otp',
      lockUntil: null,
      attemptsLeft: null,
      error: 'Code expired or not found. Tap “Resend code”.',
      resetDigits: true,
    };
  }
  return {
    phase: 'enter_otp',
    lockUntil: null,
    attemptsLeft: null,
    error: 'Could not verify. Please try again.',
    resetDigits: false,
  };
}

/** Format a lockout-until millis as a local HH:MM string. */
export function formatLockTime(ms: number | null): string {
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}
